import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Download, Calendar, User, Building, Trash2 } from 'lucide-react';

const MyIDCards = () => {
    const [myParticipants, setMyParticipants] = useState([]);
    const [filteredParticipants, setFilteredParticipants] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const navigate = useNavigate();

    useEffect(() => {
        // Load participants from localStorage
        const storedParticipants = JSON.parse(localStorage.getItem('userCreatedParticipants') || '[]');
        setMyParticipants(storedParticipants);
        setFilteredParticipants(storedParticipants);
    }, []);

    useEffect(() => {
        // Filter and sort participants
        let filtered = myParticipants.filter(participant =>
            participant.participantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            participant.eventName.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Sort participants
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case 'oldest':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case 'name':
                    return a.participantName.localeCompare(b.participantName);
                case 'event':
                    return a.eventName.localeCompare(b.eventName);
                default:
                    return 0;
            }
        });

        setFilteredParticipants(filtered);
    }, [myParticipants, searchTerm, sortBy]);

    const handleViewIDCard = (participant) => {
        navigate(`/id-card/${participant.eventId}/${participant.participantId}`);
    };

    const handleDownloadIDCard = (participant) => {
        window.open(participant.link, '_blank');
    };

    const handleDeleteParticipant = (participantToDelete) => {
        const updatedParticipants = myParticipants.filter(
            p => p.participantId !== participantToDelete.participantId
        );
        setMyParticipants(updatedParticipants);
        localStorage.setItem('userCreatedParticipants', JSON.stringify(updatedParticipants));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const groupByEvent = () => {
        const grouped = {};
        filteredParticipants.forEach(participant => {
            if (!grouped[participant.eventName]) {
                grouped[participant.eventName] = [];
            }
            grouped[participant.eventName].push(participant);
        });
        return grouped;
    };

    const groupedParticipants = groupByEvent();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">My ID Cards</h1>
                    <p className="text-gray-600">View and manage all your created ID cards</p>
                </div>

                {/* Controls */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        {/* Search */}
                        <div className="flex-1 max-w-md">
                            <input
                                type="text"
                                placeholder="Search by name or event..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                            />
                        </div>

                        {/* Sort */}
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Sort by:</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="name">Name A-Z</option>
                                <option value="event">Event A-Z</option>
                            </select>
                        </div>

                        {/* Stats */}
                        <div className="text-sm text-gray-600">
                            Total: {filteredParticipants.length} ID cards
                        </div>
                    </div>
                </div>

                {/* Content */}
                {filteredParticipants.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <User className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No ID Cards Found</h3>
                        <p className="text-gray-600 mb-6">
                            {searchTerm ? 'No ID cards match your search.' : 'You haven\'t created any ID cards yet.'}
                        </p>
                        <button
                            onClick={() => navigate('/public-create-id')}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors duration-200"
                        >
                            Create Your First ID Card
                        </button>
                    </div>
                ) : (
                    /* Grouped by Event */
                    <div className="space-y-8">
                        {Object.entries(groupedParticipants).map(([eventName, participants]) => (
                            <div key={eventName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                {/* Event Header */}
                                <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                                    <div className="flex items-center">
                                        <Building className="w-5 h-5 text-white mr-2" />
                                        <h2 className="text-xl font-semibold text-white">{eventName}</h2>
                                        <span className="ml-auto bg-white/20 text-white px-3 py-1 rounded-full text-sm">
                                            {participants.length} participant{participants.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>

                                {/* Participants Grid */}
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {participants.map((participant) => (
                                            <div
                                                key={participant.participantId}
                                                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:border-blue-300"
                                            >
                                                {/* Participant Info */}
                                                <div className="mb-4">
                                                    <h3 className="font-semibold text-gray-800 text-lg mb-1">
                                                        {participant.participantName}
                                                    </h3>
                                                    <div className="flex items-center text-sm text-gray-600 mb-2">
                                                        <Calendar className="w-4 h-4 mr-1" />
                                                        {formatDate(participant.createdAt)}
                                                    </div>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleViewIDCard(participant)}
                                                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center"
                                                    >
                                                        <Eye className="w-4 h-4 mr-1" />
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadIDCard(participant)}
                                                        className="flex-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center"
                                                    >
                                                        <Download className="w-4 h-4 mr-1" />
                                                        Download
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteParticipant(participant)}
                                                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyIDCards;
